// src/components/inventory/receiving/ReceivingForm.js
import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, Truck, Calendar, ShoppingBag, 
  PlusCircle, Package, User, X, Check, 
  CheckCircle, AlertTriangle
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { AppContext } from '../../../context/AppContext';
import { db } from '../../../firebase/config';
import { 
  collection, query, where, getDocs, doc, getDoc, 
  setDoc, serverTimestamp, updateDoc 
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

const ReceivingForm = ({ isFromPO = false }) => {
  const { poId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [poItems, setPOItems] = useState([]);
  const [formData, setFormData] = useState({
    supplier: '',
    purchaseOrder: '',
    receiveDate: new Date().toISOString().split('T')[0],
    notes: '',
    receivedBy: '',
    receivedItems: []
  });
  const [errors, setErrors] = useState({});
  
  // Fetch suppliers and purchase orders on component mount
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser || !activeBusiness) return;
      
      try {
        setLoading(true);
        
        // Fetch suppliers
        const suppliersQuery = query(
          collection(db, 'suppliers'),
          where('businessId', '==', activeBusiness.id)
        );
        
        const suppliersSnapshot = await getDocs(suppliersQuery);
        const suppliersList = [];
        
        suppliersSnapshot.forEach((doc) => {
          suppliersList.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        setSuppliers(suppliersList);
        
        // If coming from a specific PO, fetch that PO's details
        if (isFromPO && poId) {
          const poRef = doc(db, 'purchaseOrders', poId);
          const poDoc = await getDoc(poRef);
          
          if (poDoc.exists()) {
            const poData = poDoc.data();
            const supplier = suppliersList.find(s => s.id === poData.supplierId);
            
            setFormData(prev => ({
              ...prev,
              supplier: poData.supplierId,
              purchaseOrder: poId
            }));
            
            // Set selected PO
            setSelectedPO({
              id: poDoc.id,
              ...poData
            });
            
            // Set PO items
            if (poData.items && poData.items.length > 0) {
              setPOItems(poData.items);
              
              // Initialize received items with quantities from PO
              setFormData(prev => ({
                ...prev,
                receivedItems: poData.items.map(item => ({
                  itemId: item.id,
                  sku: item.sku,
                  name: item.name,
                  description: item.description,
                  orderedQuantity: item.quantity,
                  receivedQuantity: item.quantity, // Default to ordered quantity
                  acceptedQuantity: item.quantity, // Default to ordered quantity
                  damagedQuantity: 0,
                  unitPrice: item.unitPrice,
                  location: 'main-store' // Default location
                }))
              }));
            }
          }
        } else {
          // Fetch pending purchase orders
          const poQuery = query(
            collection(db, 'purchaseOrders'),
            where('businessId', '==', activeBusiness.id),
            where('status', 'in', ['pending', 'partially_received'])
          );
          
          const poSnapshot = await getDocs(poQuery);
          const poList = [];
          
          poSnapshot.forEach((doc) => {
            poList.push({
              id: doc.id,
              ...doc.data()
            });
          });
          
          setPurchaseOrders(poList);
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
        setErrors({ general: 'Failed to load data. Please try again.' });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [currentUser, activeBusiness, isFromPO, poId]);
  
  // Fetch PO details when PO selection changes
  useEffect(() => {
    const fetchPODetails = async () => {
      if (!formData.purchaseOrder) {
        setSelectedPO(null);
        setPOItems([]);
        setFormData(prev => ({ ...prev, receivedItems: [] }));
        return;
      }
      
      try {
        setLoading(true);
        
        const poRef = doc(db, 'purchaseOrders', formData.purchaseOrder);
        const poDoc = await getDoc(poRef);
        
        if (poDoc.exists()) {
          const poData = poDoc.data();
          
          setSelectedPO({
            id: poDoc.id,
            ...poData
          });
          
          // Set supplier
          setFormData(prev => ({
            ...prev,
            supplier: poData.supplierId
          }));
          
          // Set PO items
          if (poData.items && poData.items.length > 0) {
            setPOItems(poData.items);
            
            // Initialize received items with quantities from PO
            setFormData(prev => ({
              ...prev,
              receivedItems: poData.items.map(item => ({
                itemId: item.id,
                sku: item.sku,
                name: item.name,
                description: item.description,
                orderedQuantity: item.quantity,
                receivedQuantity: item.quantity, // Default to ordered quantity
                acceptedQuantity: item.quantity, // Default to ordered quantity
                damagedQuantity: 0,
                unitPrice: item.unitPrice,
                location: 'main-store' // Default location
              }))
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching PO details:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPODetails();
  }, [formData.purchaseOrder]);
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };
  
  // Handle quantity changes for received items
  const handleQuantityChange = (index, field, value) => {
    const numValue = parseInt(value) || 0;
    
    setFormData(prev => {
      const updatedItems = [...prev.receivedItems];
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: numValue
      };
      
      // If changing receivedQuantity, also update acceptedQuantity by default
      if (field === 'receivedQuantity') {
        updatedItems[index].acceptedQuantity = numValue;
      }
      
      // Ensure damaged quantity doesn't exceed received quantity
      if (field === 'damagedQuantity') {
        if (numValue > updatedItems[index].receivedQuantity) {
          updatedItems[index].damagedQuantity = updatedItems[index].receivedQuantity;
        }
        
        // Auto-adjust accepted quantity
        updatedItems[index].acceptedQuantity = 
          updatedItems[index].receivedQuantity - updatedItems[index].damagedQuantity;
      }
      
      // Ensure accepted quantity doesn't exceed received quantity
      if (field === 'acceptedQuantity') {
        if (numValue > updatedItems[index].receivedQuantity) {
          updatedItems[index].acceptedQuantity = updatedItems[index].receivedQuantity;
        }
        
        // Auto-adjust damaged quantity
        updatedItems[index].damagedQuantity = 
          updatedItems[index].receivedQuantity - updatedItems[index].acceptedQuantity;
      }
      
      return {
        ...prev,
        receivedItems: updatedItems
      };
    });
  };
  
  // Handle location changes for received items
  const handleLocationChange = (index, value) => {
    setFormData(prev => {
      const updatedItems = [...prev.receivedItems];
      updatedItems[index] = {
        ...updatedItems[index],
        location: value
      };
      
      return {
        ...prev,
        receivedItems: updatedItems
      };
    });
  };
  
  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.supplier) {
      newErrors.supplier = 'Supplier is required';
    }
    
    if (!formData.purchaseOrder) {
      newErrors.purchaseOrder = 'Purchase order is required';
    }
    
    if (!formData.receiveDate) {
      newErrors.receiveDate = 'Receive date is required';
    }
    
    if (!formData.receivedBy) {
      newErrors.receivedBy = 'Received by is required';
    }
    
    if (!formData.receivedItems.length) {
      newErrors.items = 'At least one item must be received';
    } else {
      // Check if any item has invalid quantities
      formData.receivedItems.forEach((item, index) => {
        if (item.receivedQuantity <= 0) {
          newErrors[`item_${index}_received`] = 'Received quantity must be greater than 0';
        }
        
        if (item.acceptedQuantity < 0) {
          newErrors[`item_${index}_accepted`] = 'Accepted quantity cannot be negative';
        }
        
        if (item.damagedQuantity < 0) {
          newErrors[`item_${index}_damaged`] = 'Damaged quantity cannot be negative';
        }
        
        if (item.acceptedQuantity + item.damagedQuantity !== item.receivedQuantity) {
          newErrors[`item_${index}_total`] = 'Accepted and damaged quantities must sum to received quantity';
        }
      });
    }
    
    return newErrors;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    try {
      setSaving(true);
      
      // Generate a unique ID for the receiving record
      const receivingId = uuidv4();
      
      // Calculate receipt status
      let status = 'complete';
      let totalOrdered = 0;
      let totalReceived = 0;
      
      formData.receivedItems.forEach(item => {
        totalOrdered += item.orderedQuantity;
        totalReceived += item.receivedQuantity;
      });
      
      if (totalReceived < totalOrdered) {
        status = 'partial';
      }
      
      // Create the receiving record
      const receivingData = {
        id: receivingId,
        businessId: activeBusiness.id,
        receiptNumber: `GRN-${Date.now().toString().substring(7)}`,
        poNumber: selectedPO.poNumber,
        poId: selectedPO.id,
        supplierId: formData.supplier,
        receiveDate: new Date(formData.receiveDate),
        status,
        receivedBy: formData.receivedBy,
        notes: formData.notes,
        items: formData.receivedItems,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      // Save to Firestore
      await setDoc(doc(db, 'receiving', receivingId), receivingData);
      
      // Update the purchase order status
      const poRef = doc(db, 'purchaseOrders', selectedPO.id);
      
      // Determine PO status after receiving
      let poStatus = 'received';
      if (totalReceived < totalOrdered) {
        poStatus = 'partially_received';
      }
      
      await updateDoc(poRef, {
        status: poStatus,
        updatedAt: serverTimestamp()
      });
      
      // Update inventory stock for accepted items
      for (const item of formData.receivedItems) {
        if (item.acceptedQuantity > 0) {
          // Check if item exists in inventory
          const stockQuery = query(
            collection(db, 'inventory'),
            where('businessId', '==', activeBusiness.id),
            where('sku', '==', item.sku)
          );
          
          const stockSnapshot = await getDocs(stockQuery);
          
          if (!stockSnapshot.empty) {
            // Update existing inventory item
            const stockDoc = stockSnapshot.docs[0];
            const stockData = stockDoc.data();
            
            await updateDoc(doc(db, 'inventory', stockDoc.id), {
              quantity: stockData.quantity + item.acceptedQuantity,
              updatedAt: serverTimestamp()
            });
          }
          // Note: If the item doesn't exist in inventory, we'd typically create it,
          // but that would require more information than we have in this form
        }
      }
      
      // Navigate to the new receiving record
      navigate(`/inventory/receiving/${receivingId}`);
      
    } catch (error) {
      console.error('Error saving receiving record:', error);
      setErrors({ general: 'Failed to save receiving record. Please try again.' });
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 flex justify-center items-center h-64">
        <div className="animate-spin h-10 w-10 border-4 border-[#190192] rounded-full border-t-transparent"></div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <Link
          to="/inventory/receiving"
          className="text-[#190192] hover:text-[#2C0DB5] flex items-center"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back to Receiving
        </Link>
        <h1 className="mt-2 text-xl font-bold text-gray-900">
          Receive Inventory
        </h1>
      </div>
      
      <form onSubmit={handleSubmit} className="p-6">
        {/* General Error Message */}
        {errors.general && (
          <div className="mb-6 p-4 bg-red-50 rounded-lg text-red-700 flex">
            <AlertTriangle size={20} className="mr-2 flex-shrink-0" />
            <span>{errors.general}</span>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Supplier Selection */}
          <div>
            <label htmlFor="supplier" className="block text-sm font-medium text-gray-700 mb-1">
              Supplier <span className="text-red-500">*</span>
            </label>
            <select
              id="supplier"
              name="supplier"
              value={formData.supplier}
              onChange={handleInputChange}
              disabled={isFromPO || formData.purchaseOrder}
              className={`block w-full rounded-lg border ${
                errors.supplier ? 'border-red-300' : 'border-gray-300'
              } px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20 ${
                (isFromPO || formData.purchaseOrder) ? 'bg-gray-100' : ''
              }`}
            >
              <option value="">Select Supplier</option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
            {errors.supplier && (
              <p className="mt-1 text-sm text-red-600">{errors.supplier}</p>
            )}
          </div>
          
          {/* Purchase Order Selection */}
          <div>
            <label htmlFor="purchaseOrder" className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Order <span className="text-red-500">*</span>
            </label>
            <select
              id="purchaseOrder"
              name="purchaseOrder"
              value={formData.purchaseOrder}
              onChange={handleInputChange}
              disabled={isFromPO}
              className={`block w-full rounded-lg border ${
                errors.purchaseOrder ? 'border-red-300' : 'border-gray-300'
              } px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20 ${
                isFromPO ? 'bg-gray-100' : ''
              }`}
            >
              <option value="">Select Purchase Order</option>
              {purchaseOrders
                .filter(po => !formData.supplier || po.supplierId === formData.supplier)
                .map(po => (
                  <option key={po.id} value={po.id}>
                    {po.poNumber} - {po.orderDate ? new Date(po.orderDate.seconds * 1000).toLocaleDateString() : 'No date'}
                  </option>
                ))
              }
            </select>
            {errors.purchaseOrder && (
              <p className="mt-1 text-sm text-red-600">{errors.purchaseOrder}</p>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Receive Date */}
          <div>
            <label htmlFor="receiveDate" className="block text-sm font-medium text-gray-700 mb-1">
              Receive Date <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Calendar size={16} className="text-gray-400" />
              </div>
              <input
                type="date"
                id="receiveDate"
                name="receiveDate"
                value={formData.receiveDate}
                onChange={handleInputChange}
                className={`pl-10 block w-full rounded-lg border ${
                  errors.receiveDate ? 'border-red-300' : 'border-gray-300'
                } px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20`}
              />
            </div>
            {errors.receiveDate && (
              <p className="mt-1 text-sm text-red-600">{errors.receiveDate}</p>
            )}
          </div>
          
          {/* Received By */}
          <div>
            <label htmlFor="receivedBy" className="block text-sm font-medium text-gray-700 mb-1">
              Received By <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User size={16} className="text-gray-400" />
              </div>
              <input
                type="text"
                id="receivedBy"
                name="receivedBy"
                placeholder="Enter name of person receiving"
                value={formData.receivedBy}
                onChange={handleInputChange}
                className={`pl-10 block w-full rounded-lg border ${
                  errors.receivedBy ? 'border-red-300' : 'border-gray-300'
                } px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20`}
              />
            </div>
            {errors.receivedBy && (
              <p className="mt-1 text-sm text-red-600">{errors.receivedBy}</p>
            )}
          </div>
        </div>
        
        {/* Notes */}
        <div className="mb-6">
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
            Notes (Optional)
          </label>
          <textarea
            id="notes"
            name="notes"
            rows="3"
            placeholder="Enter any additional notes about the receiving"
            value={formData.notes}
            onChange={handleInputChange}
            className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
          ></textarea>
        </div>
        
        {/* Items List */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Items to Receive</h3>
          
          {errors.items && (
            <div className="mb-4 p-3 bg-red-50 rounded-lg text-red-700 flex items-center">
              <AlertTriangle size={18} className="mr-2 flex-shrink-0" />
              <span>{errors.items}</span>
            </div>
          )}
          
          {formData.receivedItems.length === 0 ? (
            selectedPO ? (
              <div className="bg-yellow-50 p-4 rounded-lg mb-4 flex items-center">
                <AlertTriangle size={20} className="mr-2 text-yellow-500" />
                <p className="text-yellow-700">No items found in this purchase order.</p>
              </div>
            ) : (
              <div className="bg-gray-50 p-6 text-center rounded-lg">
                <Package size={40} className="mx-auto text-gray-400 mb-3" />
                <p className="text-gray-500">
                  Select a purchase order to see items to receive.
                </p>
              </div>
            )
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item
                    </th>
                    <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ordered
                    </th>
                    <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Received
                    </th>
                    <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Accepted
                    </th>
                    <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Damaged
                    </th>
                    <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {formData.receivedItems.map((item, index) => (
                    <tr key={item.itemId} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                        <div className="text-xs text-gray-500">SKU: {item.sku}</div>
                        {item.description && (
                          <div className="text-xs text-gray-500 mt-1">{item.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500 text-center">
                        {item.orderedQuantity}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <input
                          type="number"
                          min="0"
                          value={item.receivedQuantity}
                          onChange={(e) => handleQuantityChange(index, 'receivedQuantity', e.target.value)}
                          className={`w-20 text-center border ${
                            errors[`item_${index}_received`] ? 'border-red-300' : 'border-gray-300'
                          } rounded-lg px-3 py-1.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20`}
                        />
                        {errors[`item_${index}_received`] && (
                          <div className="text-xs text-red-600 mt-1">{errors[`item_${index}_received`]}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <input
                          type="number"
                          min="0"
                          max={item.receivedQuantity}
                          value={item.acceptedQuantity}
                          onChange={(e) => handleQuantityChange(index, 'acceptedQuantity', e.target.value)}
                          className={`w-20 text-center border ${
                            errors[`item_${index}_accepted`] || errors[`item_${index}_total`] 
                              ? 'border-red-300' 
                              : 'border-gray-300'
                          } rounded-lg px-3 py-1.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20`}
                        />
                        {errors[`item_${index}_accepted`] && (
                          <div className="text-xs text-red-600 mt-1">{errors[`item_${index}_accepted`]}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <input
                          type="number"
                          min="0"
                          max={item.receivedQuantity}
                          value={item.damagedQuantity}
                          onChange={(e) => handleQuantityChange(index, 'damagedQuantity', e.target.value)}
                          className={`w-20 text-center border ${
                            errors[`item_${index}_damaged`] || errors[`item_${index}_total`] 
                              ? 'border-red-300' 
                              : 'border-gray-300'
                          } rounded-lg px-3 py-1.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20`}
                        />
                        {errors[`item_${index}_damaged`] && (
                          <div className="text-xs text-red-600 mt-1">{errors[`item_${index}_damaged`]}</div>
                        )}
                        {errors[`item_${index}_total`] && (
                          <div className="text-xs text-red-600 mt-1">{errors[`item_${index}_total`]}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <select
                          value={item.location}
                          onChange={(e) => handleLocationChange(index, e.target.value)}
                          className="w-32 border border-gray-300 rounded-lg px-3 py-1.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                        >
                          <option value="main-store">Main Store</option>
                          <option value="warehouse">Warehouse</option>
                          <option value="shopfront">Shopfront</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4 border-t border-gray-200 pt-6">
          <Link
            to="/inventory/receiving"
            className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saving || formData.receivedItems.length === 0}
            className={`px-6 py-2.5 bg-[#190192] text-white font-medium rounded-lg flex items-center ${
              saving || formData.receivedItems.length === 0 
                ? 'opacity-70 cursor-not-allowed' 
                : 'hover:bg-[#2C0DB5]'
            }`}
          >
            {saving ? (
              <>
                <div className="animate-spin h-4 w-4 mr-2 border-2 border-white rounded-full border-t-transparent"></div>
                Saving...
              </>
            ) : (
              <>
                <CheckCircle size={18} className="mr-2" />
                Complete Receiving
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReceivingForm;